import $ from 'jquery';
import { defer } from 'lodash-es';
import { NO_SCROLL_TO_HASH_CLASS } from '~/lib/utils/constants';
import { getScrollingElement } from '~/lib/utils/panels';
import { contentTop, isModifierKey } from './common_utils';

const getScrollBehavior = (behavior = 'smooth') => {
  if (behavior === 'smooth' && window.matchMedia(`(prefers-reduced-motion: reduce)`).matches) {
    // Check if user prefers reduced motion, return 'auto' if true, otherwise return 'smooth'.
    // This helps support accessibility preferences for users who experience motion sickness.
    return 'auto';
  }
  return behavior;
};

/**
 * Checks if container (or document if container is not found) is scrolled
 * down all the way to the bottom.
 *
 * @param {HTMLElement} [contextElement] The element to find the scrolling element. If not provided, the default panel or document.scrollingElement is used.
 * @returns {Boolean}
 */
export const isScrolledToBottom = (contextElement) => {
  // Use clientHeight to account for any horizontal scrollbar.
  const { scrollHeight, scrollTop, clientHeight } = getScrollingElement(contextElement);

  // scrollTop can be a float, so round up to next integer.
  return Math.ceil(scrollTop + clientHeight) >= scrollHeight;
};

/**
 * Checks if container (or document if container is not found) is scrolled to the top
 *
 * @param {HTMLElement} [contextElement] The element to find the scrolling element. If not provided, the default panel or document.scrollingElement is used.
 * @returns {Boolean}
 */
export const isScrolledToTop = (contextElement) => {
  const { scrollTop } = getScrollingElement(contextElement);

  return scrollTop === 0;
};

/**
 * Scroll to the bottom of the scrolling element.
 *
 * @param {HTMLElement} [contextElement] The element to find the scrolling element. If not provided, the default panel or document.scrollingElement is scrolled.
 */
export const scrollDown = (contextElement) => {
  const scrollingElement = getScrollingElement(contextElement);
  const { scrollHeight } = scrollingElement;

  scrollingElement.scrollTo({ top: scrollHeight });
};

/**
 * Scroll to the bottom of the scrolling element.
 *
 * @param {HTMLElement} [contextElement] The element to find the scrolling element. If not provided, the default panel or document.scrollingElement is scrolled.
 */
export const scrollUp = (contextElement) => {
  getScrollingElement(contextElement).scrollTo({ top: 0 });
};

/**
 * Scrolls to the top  of the scrolling element with smooth behavior, respecting user's motion preferences.
 *
 * @param {HTMLElement} [contextElement] The element to find the scrolling element. If not provided, the default panel or document.scrollingElement is scrolled.
 */
export const smoothScrollTop = (contextElement) => {
  getScrollingElement(contextElement).scrollTo({ top: 0, behavior: getScrollBehavior() });
};

/**
 * Scrolls to the provided location.
 *
 * @param {ScrollToOptions} options The options to pass to Element.scrollTo
 * @param {HTMLElement} [contextElement] The element to find the scrolling element. If not provided, the default panel or document.scrollingElement is scrolled.
 */
export const scrollTo = (options, contextElement) => {
  getScrollingElement(contextElement).scrollTo(options);
};

/**
 * Scrolls to the top of a particular element.
 *
 * @param {jQuery | HTMLElement | String} element The target jQuery element, HTML element, or query selector to scroll to.
 * @param {Object} [options={}] Object containing additional options.
 * @param {Number} [options.offset=0] Scroll offset.
 * @param {'smooth'|'auto'|'instant'} [options.behavior='smooth'] Scroll behavior. Defaults to `smooth` unless user has `prefers-reduced-motion: reduce` enabled
 * @param {HTMLElement | String} [options.parent] The parent HTML element or query selector to scroll.
 */
export const scrollToElement = (element, options = {}) => {
  let el = element;
  if (element instanceof $) {
    // eslint-disable-next-line prefer-destructuring
    el = element[0];
  } else if (typeof el === 'string') {
    el = document.querySelector(element);
  }

  if (el && el.getBoundingClientRect) {
    // In the previous implementation, jQuery naturally deferred this scrolling.
    // Unfortunately, we're quite coupled to this implementation detail now.
    defer(() => {
      const { offset = 0, parent } = options;
      const behavior = getScrollBehavior(options?.behavior);

      let scrollContainer = getScrollingElement(el);
      if (parent && typeof parent === 'string') {
        scrollContainer = document.querySelector(parent);
      } else if (parent) {
        scrollContainer = parent;
      }
      const y = el.getBoundingClientRect().top + scrollContainer.scrollTop + offset - contentTop();

      scrollContainer.scrollTo({ top: y, behavior });
    });
  }
};

/**
 * Prevents scrolling to an element when clicking links with the URL fragment (a[href="#foo"])
 *
 * @param {PointerEvent} event Link click event
 */
export const preventScrollToFragment = (event) => {
  // Leave clicks that open the link elsewhere (new tab, new window, download) to the browser
  if (event.button || isModifierKey(event)) return;
  const link = event.target.closest('a[href]');
  if (!link?.hash) return;
  const linkUrl = new URL(link.href);
  // Query params are ignored on purpose: diff line links carry linked file params so that the
  // copied URL opens that file, but the fragment still points at a row on the current page.
  if (linkUrl.origin !== window.location.origin || linkUrl.pathname !== window.location.pathname)
    return;
  const hash = link.hash.slice(1);
  const target = document.getElementById(hash);
  if (!target) return;
  event.preventDefault();
  target.classList.add(NO_SCROLL_TO_HASH_CLASS);
  const { scrollLeft, scrollTop } = getScrollingElement(link);
  // replaceHistory won't highlight the element
  window.location.hash = hash;
  scrollTo({ top: scrollTop, left: scrollLeft }, target);
};

/**
 * Resolves the scrolling container for a list of section items.
 *
 * Strategy:
 * - Find the first existing section element by id from the provided items.
 * - Use `getScrollingElement(target)` to determine the appropriate scrollable container,
 *   which may be a panel container or `document.scrollingElement` depending on context.
 *
 * @param {Array<{id: string}>} [items=[]] List of navigation items with element ids.
 * @returns {HTMLElement|null} The scrolling container element or null if no target element is found.
 */
export const resolveScrollContainer = (items = []) => {
  const target = items.map((i) => document.getElementById(i.id)).find(Boolean);
  if (!target) return null;
  return getScrollingElement(target) || null;
};

/**
 * Determines the active section based on the current scroll position within a container.
 *
 * Strategy:
 * - If scrolled to bottom: return the last item id.
 * - Otherwise: iterate items in order and return the first section whose top edge has not yet
 *   scrolled above the container's top edge (rect.top >= cRect.top + 1).
 * - Fallback: return null if all sections are above the viewport.
 *
 * @param {Array<{id: string}>} [items=[]] List of sections in visual order.
 * @param {HTMLElement} container The scrolling container being observed.
 * @returns {string|null} The id of the active section or null if no change is needed.
 */
export const computeActiveSection = (items = [], container) => {
  if (!items.length || !container) return null;

  const cRect = container.getBoundingClientRect
    ? container.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight || 0 };

  const hasScrollMetrics =
    typeof container.scrollTop === 'number' &&
    typeof container.clientHeight === 'number' &&
    typeof container.scrollHeight === 'number';

  if (hasScrollMetrics) {
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
    if (atBottom) {
      return items[items.length - 1]?.id || null;
    }
  }

  for (const item of items) {
    const el = document.getElementById(item.id);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.top >= cRect.top + 1) {
        return item.id;
      }
    }
  }

  return null;
};
